/**
 * Audit Logs UI Types
 *
 * Type definitions for audit log display and filtering.
 */

import type {
  AuditEventCategory,
  AuditEventType,
  AuditSeverity,
  AuditActor,
  AuditTarget,
  AuditChanges,
  AuditSource,
  Environment,
} from "@tatvex/types";

// ============================================================================
// Audit Log Display Types
// ============================================================================

/** Audit log entry for display */
export interface AuditLogEntry {
  readonly id: string;
  readonly category: AuditEventCategory;
  readonly eventType: AuditEventType;
  readonly actor: AuditActor;
  readonly target: AuditTarget;
  readonly changes?: AuditChanges;
  readonly metadata?: Record<string, unknown>;
  readonly source: AuditSource;
  readonly timestamp: string;
  readonly severity: AuditSeverity;
  readonly environment: Environment;
}

/** Audit logs list response */
export interface AuditLogsResponse {
  readonly items: readonly AuditLogEntry[];
  readonly total: number;
  readonly hasMore: boolean;
}

// ============================================================================
// Filter Types
// ============================================================================

/** Filter options for audit logs */
export interface AuditFilters {
  readonly dateFrom?: string;
  readonly dateTo?: string;
  readonly actorId?: string;
  readonly category?: AuditEventCategory | "all";
  readonly eventType?: string; // AuditEventType or "all"
  readonly targetType?: string | "all";
  readonly severity?: AuditSeverity | "all";
  readonly environment?: Environment | "all";
  readonly page?: number;
  readonly pageSize?: number;
}

/** Export format options */
export type ExportFormat = "csv" | "json";

// ============================================================================
// UI State Types
// ============================================================================

/** Live mode state */
export interface LiveModeState {
  readonly enabled: boolean;
  readonly paused: boolean;
  readonly lastFetchTime?: string;
}

/** Expanded row state */
export type ExpandedRows = Set<string>;

// ============================================================================
// Constants
// ============================================================================

/** Event category labels */
export const EVENT_CATEGORY_LABELS: Record<AuditEventCategory, string> = {
  auth: "Authentication",
  config: "Configuration",
  data: "Data",
  promotion: "Promotion",
  tenant: "Tenant",
  user: "User",
  system: "System",
};

/** Event category colors */
export const EVENT_CATEGORY_COLORS: Record<AuditEventCategory, string> = {
  auth: "blue",
  config: "green",
  data: "purple",
  promotion: "amber",
  tenant: "orange",
  user: "cyan",
  system: "gray",
};

/** Severity labels and colors */
export const SEVERITY_INFO: Record<AuditSeverity, { label: string; color: string }> = {
  info: { label: "Info", color: "muted" },
  warning: { label: "Warning", color: "warning" },
  critical: { label: "Critical", color: "error" },
};

// ============================================================================
// Webhook Delivery Types
// ============================================================================

/** Delivery status summary for an alert configuration */
export interface AlertDeliveryInfo {
  readonly lastTriggeredAt: string | null;
  readonly lastStatus: "success" | "failed" | "pending" | null;
  readonly totalDeliveries: number;
}

/** Actor type labels */
export const ACTOR_TYPE_LABELS: Record<string, string> = {
  user: "User",
  system: "System",
  api_key: "API Key",
  scheduler: "Scheduler",
};

/** All event types grouped by category */
export const EVENT_TYPES_BY_CATEGORY: Record<AuditEventCategory, readonly string[]> = {
  auth: [
    "auth.login",
    "auth.logout",
    "auth.token_refresh",
    "auth.token_revoked",
    "auth.failed_login",
    "auth.password_changed",
  ],
  config: [
    "config.created",
    "config.updated",
    "config.promoted",
    "config.archived",
    "config.rollback",
    "config.validated",
    "config.draft_saved",
    "config.published",
    "config.rolled_back",
  ],
  data: [
    "data.product_created",
    "data.product_updated",
    "data.product_deleted",
    "data.products_uploaded",
    "data.provider_created",
    "data.provider_updated",
    "data.provider_deleted",
  ],
  tenant: [
    "tenant.created",
    "tenant.updated",
    "tenant.suspended",
    "tenant.activated",
    "tenant.deleted",
  ],
  user: [
    "user.created",
    "user.updated",
    "user.suspended",
    "user.activated",
    "user.deleted",
    "user.role_changed",
    "user.invited",
  ],
  promotion: [
    "promotion.requested",
    "promotion.approved",
    "promotion.rejected",
    "promotion.executed",
  ],
  system: ["system.migration_run", "system.error", "system.maintenance"],
};

/** Common target types */
export const TARGET_TYPES: readonly string[] = [
  "user",
  "tenant",
  "config",
  "product",
  "provider",
  "vertical",
  "session",
];

/** Default page size */
export const DEFAULT_PAGE_SIZE = 50;

/** Maximum export records */
export const MAX_EXPORT_RECORDS = 10000;

/** Live mode polling interval (ms) */
export const LIVE_MODE_INTERVAL = 2000;

/** Format event type for display */
export function formatEventType(eventType: string): string {
  return eventType
    .replace(/\./g, " ")
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/** Format timestamp for display */
export function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/** Format timestamp for relative display */
export function formatRelativeTime(timestamp: string): string {
  const now = Date.now();
  const time = new Date(timestamp).getTime();
  const diff = now - time;

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;

  return formatTimestamp(timestamp);
}
